/* RegistryWatchView — Veille registre (onglet Radar).
 * Surveille les entités de l'Annuaire au registre officiel (FOSC/SHAB Suisse) :
 * mutations, inscriptions, radiations, faillites.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Landmark, Loader2, RefreshCw, Plus, X, ExternalLink, Eye, Search,
} from "lucide-react";

const KIND_COLORS = {
  HR01: "#1D9E75", HR02: "#378ADD", HR03: "#E24B4A", KK: "#E24B4A",
};

export function RegistryWatchView() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterEntity, setFilterEntity] = useState("");
  const [picker, setPicker] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [pickerItems, setPickerItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, eRes] = await Promise.all([
        api.get("/workspace/registry-watch"),
        api.get("/workspace/registry-watch/events", { params: { entity_id: filterEntity || undefined } }),
      ]);
      setStatus(sRes.data || null);
      setEvents(eRes.data?.items || []);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEntity]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/workspace/registry-watch/refresh");
      notify.success(`Vérifié : ${r.data?.stats?.checked || 0} entité(s), ${r.data?.stats?.new_events || 0} nouvel(s) événement(s).`);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
    setRefreshing(false);
  };

  const searchEntities = async (q) => {
    setPickerQ(q);
    if (q.length < 2) { setPickerItems([]); return; }
    try {
      const r = await api.get("/workspace/annuaire/entities", { params: { search: q, limit: 8 } });
      setPickerItems(r.data?.items || r.data || []);
    } catch { setPickerItems([]); }
  };

  const toggleWatch = async (entityId, enabled) => {
    try {
      await api.post(`/workspace/annuaire/entities/${entityId}/registry-watch`, { enabled });
      notify.success(enabled ? "Surveillance activée — première vérification en cours…" : "Surveillance désactivée.");
      setPicker(false); setPickerQ(""); setPickerItems([]);
      setTimeout(load, 1500);
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="registry-watch-root">
      <div className="noam-card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-medium text-zinc-100 flex items-center gap-2">
              <Landmark className="w-4 h-4 text-[#378ADD]" /> Veille registre officiel
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {status?.source || "FOSC / SHAB (Suisse)"} · mutations, inscriptions, radiations, faillites des entités surveillées.
              {status?.last_checked_at && ` Dernière vérification : ${status.last_checked_at.slice(0, 16).replace("T", " ")}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPicker((p) => !p)} className="btn-secondary text-xs" data-testid="registry-add-btn">
              <Plus className="w-3.5 h-3.5" /> Surveiller une entité
            </button>
            <button onClick={refresh} disabled={refreshing} className="btn-primary text-xs disabled:opacity-50" data-testid="registry-refresh-btn">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Vérifier maintenant
            </button>
          </div>
        </div>

        {picker && (
          <div className="mt-3 bg-black/30 border border-white/10 rounded p-3" data-testid="registry-picker">
            <div className="flex items-center gap-2 mb-2">
              <Search className="w-3.5 h-3.5 text-zinc-500" />
              <input autoFocus value={pickerQ} onChange={(e) => searchEntities(e.target.value)}
                placeholder="Rechercher dans l'Annuaire…"
                className="bg-transparent border-0 outline-none text-xs w-full text-zinc-200"
                data-testid="registry-picker-input" />
              <button onClick={() => setPicker(false)} className="text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </div>
            {pickerItems.map((e) => (
              <button key={e.id} onClick={() => toggleWatch(e.id, true)}
                className="w-full text-left text-xs text-zinc-300 hover:text-white hover:bg-white/5 rounded px-2 py-1.5 flex items-center gap-2"
                data-testid={`registry-pick-${e.id}`}>
                <Eye className="w-3 h-3 text-[#378ADD]" /> {e.name || e.nom_organisation}
                <span className="text-zinc-600 text-[10px]">{e.type_organisation || e.type || ""}</span>
              </button>
            ))}
          </div>
        )}

        {/* Entités surveillées */}
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="registry-watched-list">
          {(status?.watched || []).length === 0 && (
            <span className="text-xs text-zinc-600">Aucune entité surveillée. Ajoutez-en une pour être alerté de ses mutations au registre.</span>
          )}
          {(status?.watched || []).map((w) => (
            <span key={w.id}
              className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1.5 cursor-pointer transition-colors ${filterEntity === w.id ? "border-[#378ADD] text-white bg-[#378ADD]/15" : "border-white/10 text-zinc-300 hover:border-white/25"}`}
              onClick={() => setFilterEntity(filterEntity === w.id ? "" : w.id)}
              data-testid={`registry-watched-${w.id}`}>
              {w.name || w.nom_organisation}
              <span className="text-[9px] text-zinc-500">{w.events_count}</span>
              <X className="w-3 h-3 text-zinc-600 hover:text-red-400"
                onClick={(ev) => { ev.stopPropagation(); toggleWatch(w.id, false); }} />
            </span>
          ))}
        </div>
      </div>

      {/* Événements */}
      <div className="noam-card p-0" data-testid="registry-events-card">
        {loading ? (
          <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-zinc-600 text-sm">
            Aucun événement au registre pour l'instant. Les publications apparaîtront ici dès qu'une entité surveillée bouge.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {events.map((ev) => {
              const color = KIND_COLORS[ev.sub_rubric] || KIND_COLORS[ev.rubric] || "#EF9F27";
              return (
                <div key={`${ev.entity_id}-${ev.publication_id}`} className="p-3 flex items-start gap-3" data-testid={`registry-event-${ev.publication_id}`}>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                    style={{ color, background: `${color}18`, border: `1px solid ${color}44` }}>
                    {ev.kind_label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-100">{ev.title}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {ev.entity_name} · {ev.published_date}{ev.canton ? ` · ${ev.canton}` : ""} · {ev.source_label}
                    </div>
                  </div>
                  <a href={ev.url} target="_blank" rel="noreferrer"
                    className="text-zinc-500 hover:text-[#378ADD] p-1 shrink-0" title="Voir la publication officielle">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
